import "./lib/storageShim";
import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";

// ── Sentry error monitoring ─────────────────────────────────────
// Silently disabled when REACT_APP_SENTRY_DSN is unset (local dev, etc.)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
    // Conservative sample rate to keep free-tier quota healthy
    tracesSampleRate: 0.1,
    // Default integrations are enough for unhandled errors + fetch breadcrumbs;
    // we skip replay to keep bundle small.
    sendDefaultPii: false,
    integrations: [
      // User feedback widget — the auto-injected floating button used to
      // collide with our floating Play button on Android (both at bottom-right
      // on top of each other inside Sentry's shadow DOM). We disable auto-inject
      // here and attach the widget to our own header "Report a Bug" button
      // programmatically — see `openFeedbackDialog` exported below.
      Sentry.feedbackIntegration({
        colorScheme: "dark",
        autoInject: false,
        showBranding: false,
        buttonLabel: "Report a problem",
        submitButtonLabel: "Send report",
        formTitle: "Report a problem",
        messagePlaceholder: "What happened? Steps to reproduce help us fix it faster.",
        successMessageText: "Thanks — we got it and we'll look into it.",
        themeDark: {
          background: "#1a1530",
          foreground: "#e2e0ea",
          accentBackground: "linear-gradient(90deg, #8b5cf6, #06b6d4)",
          accentForeground: "#ffffff",
          successColor: "#22d3ee",
          errorColor: "#f87171",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
          outline: "1px solid rgba(139, 92, 246, 0.4)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        },
      }),
    ],
    ignoreErrors: [
      // Browser noise
      "ResizeObserver loop",
      "Non-Error promise rejection captured",
      // User-triggered autoplay policy errors — expected on some browsers
      "play() failed because the user didn't interact",
    ],
  });
}

function FallbackUI() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#0f0a1f",
        color: "#e2e0ea",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        padding: 24,
      }}
      data-testid="sentry-fallback"
    >
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h1>
      <p style={{ color: "#a8a0ba", fontSize: 14, marginBottom: 20 }}>
        Our team has been notified. Please refresh the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: "linear-gradient(90deg, #8b5cf6, #06b6d4)",
          color: "white",
          border: "none",
          padding: "12px 24px",
          borderRadius: 10,
          fontWeight: 600,
          cursor: "pointer",
        }}
        data-testid="sentry-reload-btn"
      >
        Reload
      </button>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<FallbackUI />}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
